// Custom Block Library — Phase 2 browser client (DB-backed public user blocks + publish/report flow).
//
// Kept SEPARATE from customBlockLibraryService.ts (which stays pure/curated so the node CI bundle has no
// Supabase dependency). This module talks to Supabase + the block-publish edge function. The library
// gallery merges curatedEntries() (pure) with fetchPublicUserBlocks() (here). All trust-state changes go
// through the edge function — the client NEVER writes tier flags directly (RLS forbids it anyway).

import { supabase } from './supabaseClient';
import { callEdgeFunction } from '../utils/supabaseFunctions';
import { curatedEntries, type LibraryFilters } from './customBlockLibraryService';
import type { BlockSubmission, LibraryEntry } from '../types/customBlockLibrary';

/** Optimizer_safe public user blocks + curated optimizer_safe — the optimizer mutation pool (Phase 4).
 *  RLS + the filters drop anything not public+published, so disabled blocks vanish immediately. */
export async function fetchOptimizerSafePool(): Promise<LibraryEntry[]> {
  const curated = curatedEntries().filter((e) => e.tier.optimizerSafe);
  const { data, error } = await supabase
    .from('custom_block_templates')
    .select('id, author_id, category, block_type, name, description, tags, latest_version, public_visible, ai_safe, optimizer_safe, status, downloads, custom_block_versions(version, code, tsl_ast, param_schema)')
    .eq('source', 'user').eq('public_visible', true).eq('status', 'published').eq('optimizer_safe', true)
    .limit(200);
  if (error || !data) return curated;
  const user = (data as unknown as TemplateRow[]).map(rowToEntry).filter((e): e is LibraryEntry => !!e);
  return [...curated, ...user];
}

interface VersionRow { version: number; code: string | null; tsl_ast?: unknown; param_schema: Record<string, unknown> }
interface TemplateRow {
  id: string; author_id: string; category: LibraryEntry['category']; block_type: LibraryEntry['blockType'];
  name: string; description: string; tags: string[]; latest_version: number;
  public_visible: boolean; ai_safe: boolean; optimizer_safe: boolean; status: LibraryEntry['status'];
  downloads: number; custom_block_versions: VersionRow[];
}

function rowToEntry(r: TemplateRow): LibraryEntry | null {
  const v = (r.custom_block_versions ?? []).reduce<VersionRow | null>((best, cur) => (!best || cur.version > best.version ? cur : best), null);
  if (!v) return null; // published head with no readable version → skip
  return {
    id: r.id, source: 'user', category: r.category, blockType: r.block_type, name: r.name,
    description: r.description, tags: r.tags ?? [], version: v.version,
    tier: { publicVisible: r.public_visible, aiSafe: r.ai_safe, optimizerSafe: r.optimizer_safe },
    code: v.code ?? '', tslAst: (v.tsl_ast as LibraryEntry['tslAst']) ?? undefined, paramSchema: (v.param_schema ?? {}) as LibraryEntry['paramSchema'],
    author: r.author_id, downloads: r.downloads, status: r.status,
  };
}

/** Public, published USER blocks (joined with their latest readable version). RLS already restricts
 *  reads to public+published rows, so a delisted/disabled block disappears here automatically. */
export async function fetchPublicUserBlocks(filters?: LibraryFilters): Promise<LibraryEntry[]> {
  let q = supabase
    .from('custom_block_templates')
    .select('id, author_id, category, block_type, name, description, tags, latest_version, public_visible, ai_safe, optimizer_safe, status, downloads, custom_block_versions(version, code, tsl_ast, param_schema)')
    .eq('source', 'user').eq('public_visible', true).eq('status', 'published');
  if (filters?.category) q = q.eq('category', filters.category);
  if (filters?.blockType) q = q.eq('block_type', filters.blockType);
  const { data, error } = await q.limit(200);
  if (error || !data) return [];
  let out = (data as unknown as TemplateRow[]).map(rowToEntry).filter((e): e is LibraryEntry => !!e);
  if (filters?.query) {
    const s = filters.query.trim().toLowerCase();
    out = out.filter((e) => e.name.toLowerCase().includes(s) || e.description.toLowerCase().includes(s) || e.tags.some((t) => t.toLowerCase().includes(s)));
  }
  return out;
}

/** The full library the gallery shows: curated (pure) + public user blocks (DB). Curated first. */
export async function loadLibrary(filters?: LibraryFilters): Promise<LibraryEntry[]> {
  const [curated, user] = await Promise.all([
    Promise.resolve(curatedEntries(filters)),
    fetchPublicUserBlocks(filters).catch(() => [] as LibraryEntry[]),
  ]);
  return [...curated, ...user];
}

// ── mutations (all via the edge function; service-role does the privileged writes) ───────────────────

async function callPublish(body: Record<string, unknown>): Promise<{ ok: boolean; status: number; data: any }> {
  const res = await callEdgeFunction('block-publish', body);
  let data: any = null; try { data = await res.json(); } catch { /* non-JSON */ }
  return { ok: res.ok, status: res.status, data };
}

/** Create a private draft head row (metadata only; code is submitted at publish time). */
export async function createDraft(meta: { category: LibraryEntry['category']; blockType: LibraryEntry['blockType']; name: string; description: string; tags?: string[]; forkedFrom?: { id: string; version: number } }): Promise<string | null> {
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase.from('custom_block_templates').insert({
    author_id: uid, source: 'user', category: meta.category, block_type: meta.blockType,
    name: meta.name, description: meta.description, tags: meta.tags ?? [],
    public_visible: false, ai_safe: false, optimizer_safe: false, status: 'draft',
    // Fork derivation provenance (additive, nullable — see migration 0072). Only set for forks, so an
    // original (non-fork) draft never references the new columns and works whether or not 0072 is applied.
    ...(meta.forkedFrom ? { forked_from_id: meta.forkedFrom.id, forked_from_version: meta.forkedFrom.version } : {}),
  }).select('id').single();
  return error ? null : (data?.id ?? null);
}

/** Publish (or re-version) a draft: runs the gate server-side; resolves with the decision. */
export function publishBlock(blockId: string, submission: BlockSubmission, version?: number) {
  return callPublish({ action: 'publish', blockId, submission, version });
}
export function unpublishBlock(blockId: string) { return callPublish({ action: 'unpublish', blockId }); }
export function reportBlock(blockId: string, reason: 'unsafe' | 'incorrect' | 'misleading' | 'plagiarism' | 'other', detail?: string, version?: number) {
  return callPublish({ action: 'report', blockId, reason, detail, version });
}
/** Admin trust-state changes (server enforces is_admin). */
export function adminSetBlockState(blockId: string, action: 'delist' | 'disable' | 'reinstate', reason?: string, reportId?: string) {
  return callPublish({ action, blockId, reason, reportId });
}

export interface AdminReport {
  id: string; block_id: string; version: number | null; reporter_id: string;
  reason: string; detail: string | null; status: string; created_at: string;
  block: { id: string; name: string; category: string; status: string; public_visible: boolean; author_id: string } | null;
}
/** Admin: list open/reviewed reports (server enforces is_admin). */
export async function adminListReports(): Promise<AdminReport[]> {
  const r = await callPublish({ action: 'list_reports' });
  return r.ok && Array.isArray(r.data?.reports) ? r.data.reports : [];
}

/** The set of library block ids that are security_disabled (or legacy disabled) — must FAIL CLOSED at
 *  execution everywhere. Backed by the 0070 revocation read policy. Empty on error (caller decides;
 *  callers that need a hard guarantee should treat a failed fetch as a reason to block, not allow). */
export async function fetchSecurityDisabledIds(): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('custom_block_templates')
    .select('id, status').in('status', ['security_disabled', 'disabled']);
  if (error || !data) return new Set();
  return new Set((data as Array<{ id: string }>).map((r) => r.id));
}

/** Of the given library block ids, which are STILL public+published. A disabled/delisted/unpublished
 *  block falls out of the public set (RLS hides the row from non-authors), so callers treat any id NOT
 *  returned here as "no longer available" — the signal used to warn in strategies that inlined it. */
export async function fetchAvailableLibraryIds(ids: string[]): Promise<Set<string>> {
  const userIds = [...new Set(ids)].filter(Boolean);
  if (!userIds.length) return new Set();
  const { data, error } = await supabase
    .from('custom_block_templates')
    .select('id').in('id', userIds).eq('public_visible', true).eq('status', 'published');
  if (error || !data) return new Set();
  return new Set((data as Array<{ id: string }>).map((r) => r.id));
}
